// useSyncedDetail — единый резолвер состояния detail-экрана, читающего из
// локальной SQLite (PowerSync) с опциональным серверным фолбэком.
//
// До этого хука связка `resolveSyncedDetailPhase` + `useAwaitingSync` +
// `remoteStatusFromQuery` была собрана вручную только в двух местах из десяти
// (материалы и коллекции). Остальные детали — конспект, набор карточек, тест,
// запись, чат, план — писали ad-hoc логику, и каждая ошибалась по-своему: где-то
// пустой рендер, где-то вечный скелетон, где-то ErrorState на полностью
// доступной локально сущности.

import {
	type RemoteStatus,
	resolveSyncedDetailPhase,
	type SyncedDetailPhase,
} from "./resolve-synced-detail-phase.js";
import { useAwaitingSync } from "./use-awaiting-sync.js";
import { useOnlineStatus } from "./use-online-status.js";
import { usePowerSyncStatus } from "./use-powersync-status.js";

export interface SyncedDetailInput {
	/** Сущность найдена (локально или из серверного фолбэка). */
	hasData: boolean;
	/** Локальный PowerSync-запрос ещё выполняется. */
	localLoading?: boolean;
	/** Ошибка локального SQL (редко). */
	localError?: boolean;
	/** Состояние серверного фолбэка, если он есть. */
	remoteStatus?: RemoteStatus;
	/** Окно ожидания read-after-write (по умолчанию 6с после первичного синка). */
	graceMs?: number;
}

export interface SyncedDetailState {
	phase: SyncedDetailPhase;
	/** Показать скелетон. */
	isLoading: boolean;
	/** Данных нет и не будет без сети — локальной копии на устройстве нет. */
	isOfflineWithoutCopy: boolean;
	isNotFound: boolean;
	isFailed: boolean;
}

export function useSyncedDetail(input: SyncedDetailInput): SyncedDetailState {
	const { hasData, localLoading = false, localError = false, remoteStatus = "idle" } = input;
	const isOnline = useOnlineStatus();
	const { hasSynced } = usePowerSyncStatus();
	const awaitingSync = useAwaitingSync(hasData, input.graceMs);

	const phase = resolveSyncedDetailPhase({
		hasData,
		awaitingSync,
		hasSynced,
		isOnline,
		remoteStatus,
		localError,
	});

	// Пока первый локальный запрос не отработал, «не найдено» — преждевременный
	// вывод: строка может быть в SQLite, просто запрос ещё в полёте.
	const effective: SyncedDetailPhase = phase === "not_found" && localLoading ? "loading" : phase;

	return {
		phase: effective,
		isLoading: effective === "loading",
		isOfflineWithoutCopy: effective === "sync_pending",
		isNotFound: effective === "not_found",
		isFailed: effective === "load_failed",
	};
}
